import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import Page from "components/page/Page";
import { t } from 'i18next';
import { GetAudiences, GetEmailCampaigns, IAudience, IEmailCampaign, PostEmailCampaign, PutPublishEmailCampaign, PutScheduleEmailCampaign, PutStep1EmailCampaign, PutStep2EmailCampaign } from "api/rpc/2024-04/facilityAdmin/marketing/emailCampaigns";
import { useDispatch } from "react-redux";
import Card from "components/card/Card";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input";
import { ButtonNew } from "components/buttonNew";
import { StatusCode } from "api/protocols";
import { showError, showSuccess } from "redux/actions/ui";
import { useHistory, useParams, useLocation } from "react-router";
import Accordion from "./Accordion";
import { useUrlParams } from "hooks/useUrlParams/useUrlParams";
import Sheet from "components/sheet/Sheet";
import AudienceCustomersTable from "./AudienceCustomersTable";
import SelectNew from "components/select/SelectNew";
import {
  emailCampaignBuilderPath,
  emailCampaignBuilderReturnPath,
  prepareEmailCampaignBuilderLaunch,
  type TEmailBuilderLocationState,
} from "pages/secure/facility/marketing/emailBuilder/launchEmailCampaignBuilder";
import { emailBuilderCampaignNeedsTemplateSelection } from "pages/secure/facility/marketing/emailBuilder/emailBuilderCampaignEntry";
import { getEmailCampaignDesignStatus } from "./getEmailCampaignDesignStatus";
import EmailCampaignTemplatePicker from "./EmailCampaignTemplatePicker";
import {
  emailCampaignDesignCtaLabel,
  emailCampaignDesignStepBadgeType,
  emailCampaignDesignStepPreviewLabel,
  resolveEmailCampaignContinueDestination,
  resolveEmailCampaignFinalPublishDecision,
} from "./emailCampaignDesignStatus";
import EmailCampaignSchedulePanel from "./EmailCampaignSchedulePanel";
import EmailCampaignPublishSuccess from "./EmailCampaignPublishSuccess";
import {
  buildEmailCampaignSchedulePayloadFromCampaign,
  isEmailCampaignScheduleCompleteFromCampaign,
  TEmailCampaignScheduleFields,
} from "./emailCampaignScheduleState";
import {
  emailCampaignAccordionSectionLifecycle,
  type TEmailCampaignAccordionSnapshots,
} from "./emailCampaignAccordionSectionSnapshots";
type TCreateEmailCampaign = Omit<
  IEmailCampaign,
  "scheduled_type" | "scheduled_at" | "scheduled_recurring_type"
> &
  TEmailCampaignScheduleFields;

interface IPageState {
  campaign: TCreateEmailCampaign;
}

enum Steps {
  Overview = 1,
  Details = 2,
  SelectTemplate = 3,
  Schedule = 4,
  Success = 5,
}

const TEXT_PREVIEW_MAX_LENGTH = 255;

function parseCreateStep(value: string | null): Steps {
  const step = Number(value);
  if (step >= Steps.Overview.valueOf() && step <= Steps.Success.valueOf()) {
    return step as Steps;
  }

  return Steps.Overview;
}

function isPublishedEmailCampaignStatus(status: IEmailCampaign["status"] | undefined): boolean {
  return status === "published" || status === "pending" || status === "scheduled";
}

function createCampaignTemplatePickerReturnTo(campaignId: number, locationState: unknown): string {
  if (typeof locationState === "object" && locationState !== null) {
    const returnTo = (locationState as TEmailBuilderLocationState).returnTo;
    if (typeof returnTo === "string" && returnTo.length > 0) {
      return returnTo;
    }
  }

  return emailCampaignBuilderReturnPath(campaignId, "create");
}

export default function CreateEmailCampaign() {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { id: campaignId } = useParams<{ id: string }>();
  const isEdit = campaignId !== undefined;
  const { searchParams, setSearchParam } = useUrlParams({ step: '1' });
  const stepNumber = parseCreateStep(searchParams.get('step'));

  function goToStep(step: Steps) {
    setSearchParam('step', String(step));
  }

  const [audiences, setAudiences] = useState<IAudience[]>([]);
  const [segmentModalOpen, setSegmentModalOpen] = useState<boolean>(false);
  const [selectedAudienceIds, setSelectedAudienceIds] = useState<number[]>([]);
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [schedulePublishing, setSchedulePublishing] = useState(false);
  const [creatingCampaign, setCreatingCampaign] = useState(false);
  const accordionSnapshotsRef = useRef<TEmailCampaignAccordionSnapshots>({});

  const [state, setState] = useState<IPageState>({
    campaign: {
      id: 0,
      name: '',
      type: 'email',
      status: 'draft',
      description: '',
      subject: '',
      text_preview: '',
      content: '',
      from_name: '',
      from_address: '',
      scheduled_type: null,
      scheduled_at: null,
      scheduled_recurring_type: null,
    },
  });

  const continueAction = {
    content: t('Continue'),
    action: nextStep,
  };

  const stepInfo = [
    {
      title: t('Campaign overview'),
      subtitle: t('Name your campaign and add a brief description.'),
      form_title: t('Overview'),
      primaryAction: continueAction,
      breadcrumbs: [],
    },
    {
      title: t('Set campaign details'),
      subtitle: t('Set the basics—audience, sender, subject, and send time.'),
      form_title: t('Campaign details'),
      primaryAction: continueAction,
      breadcrumbs: [
        {
          label: t('Campaign details'),
          url: `/admin/email-marketing/campaigns/${campaignId}/new?step=2`,
        },
      ],
    },
    {
      title: t('Select a template'),
      subtitle: t('Select a layout to start building your email content.'),
      breadcrumbs: [
        {
          label: t('Campaign details'),
          url: `/admin/email-marketing/campaigns/${campaignId}/new?step=2`,
        },
        {
          label: t('Design email'),
          url: `/admin/email-marketing/campaigns/${campaignId}/new?step=3`,
        },
      ],
    },
    {
      title: t('Schedule email'),
      subtitle: t('Choose when your campaign will be delivered to your audience.'),
      primaryAction: {
        content: schedulePublishing ? t("Scheduling…") : t("Schedule campaign"),
        action: () => {
          void publishCampaign();
        },
        disabled: !(isEmailCampaignScheduleCompleteFromCampaign(state.campaign) && !schedulePublishing && !scheduleSaving),
      },
      breadcrumbs: [
        {
          label: t('Campaign details'),
          url: `/admin/email-marketing/campaigns/${campaignId}/new?step=2`,
        },
        {
          label: t('Schedule email'),
          url: `/admin/email-marketing/campaigns/${campaignId}/new?step=4`,
        },
      ],
    },
    {
      title: state.campaign.name || t('Campaign created'),
      breadcrumbs: [
        {
          label: t('Campaign created'),
        },
      ],
    },
  ];

  async function update() {
    if (stepNumber === Steps.Overview) {
      return updateStep1();
    } else if (stepNumber === Steps.Details) {
      return updateStep2();
    } else if (stepNumber === Steps.SelectTemplate) {
      return true;
    } else if (stepNumber === Steps.Schedule) {
      return updateSchedule();
    } else if (stepNumber === Steps.Success) {
      return true;
    }

    return false;
  }

  async function updateStep1() {
    const resp = await PutStep1EmailCampaign({
      id: state.campaign.id,
      name: state.campaign.name,
      description: state.campaign.description,
    }, false);

    if (resp.status !== StatusCode.OK) {
      dispatch(showError(resp.data.message));
      return false;
    }

    dispatch(showSuccess(t('Email campaign updated')));
    return true;
  }

  async function updateStep2(notifySuccess: boolean = true) {
    if (selectedAudienceIds.length < 1) {
      dispatch(showError(t('A smart list is required.')));
      return false;
    }

    const resp = await PutStep2EmailCampaign({
      id: state.campaign.id,
      audience_ids: selectedAudienceIds,

      from_name: state.campaign.from_name ?? '',
      from_address: state.campaign.from_address ?? '',

      subject: state.campaign.subject ?? '',
      text_preview: state.campaign.text_preview ?? '',
    }, true);

    if (resp.status !== StatusCode.OK) {
      dispatch(showError(resp.data.message));
      return false;
    }

    setState(prev => ({
      ...prev,
      campaign: {
        ...prev.campaign,
        ...resp.data,
        audiences: resp.data.audiences ?? prev.campaign.audiences,
      },
    }));
    setSelectedAudienceIds(resp.data.audiences?.map(a => a.id) ?? []);

    if (notifySuccess) {
      dispatch(showSuccess(t('Email campaign updated')));
    }
    return true;
  }

  function updateCampaignSchedule(fields: TEmailCampaignScheduleFields) {
    console.log(fields);
    setState(prev => ({
      ...prev,
      campaign: {
        ...prev.campaign,
        ...fields,
      },
    }));
  }

  async function updateSchedule(notifySuccess: boolean = true) {
    const payload = buildEmailCampaignSchedulePayloadFromCampaign(state.campaign.id, state.campaign);
    if (payload == null) {
      dispatch(showError(t("Choose when to send this campaign.")));
      return false;
    }

    setScheduleSaving(true);
    try {
      const resp = await PutScheduleEmailCampaign(payload, true);
      if (resp.status !== StatusCode.OK) {
        dispatch(showError(resp.message || t("Could not save schedule settings.")));
        return false;
      }

      if (resp.data != null && typeof resp.data === "object" && "id" in resp.data) {
        setState(prev => ({
          ...prev,
          campaign: {
            ...prev.campaign,
            ...resp.data,
          },
        }));
      }

      if (notifySuccess) {
        dispatch(showSuccess(t("Schedule settings saved")));
      }
      return true;
    } finally {
      setScheduleSaving(false);
    }
  }

  async function finalizePublish() {
    if (!(await updateSchedule(false))) {
      return;
    }

    setSchedulePublishing(true);
    try {
      const resp = await PutPublishEmailCampaign({ id: state.campaign.id }, true);
      if (resp.status !== StatusCode.OK) {
        dispatch(showError(resp.message));
        return;
      }

      if (resp.data != null && typeof resp.data === "object" && "id" in resp.data) {
        setState(prev => ({
          ...prev,
          campaign: {
            ...prev.campaign,
            ...resp.data,
          },
        }));
      }

      goToStep(Steps.Success);
    } finally {
      setSchedulePublishing(false);
    }
  }

  async function publishCampaign() {
    if (!isEmailCampaignScheduleCompleteFromCampaign(state.campaign) || schedulePublishing || scheduleSaving) {
      return;
    }

    const publishDecision = resolveEmailCampaignFinalPublishDecision({
      design: state.campaign.design,
    });

    if (publishDecision !== "allowed") {
      dispatch(
        showError(
          publishDecision === "design_required"
            ? t("Design the email in the email builder before publishing this campaign.")
            : t("Finalize your email design in the builder before publishing this campaign."),
        ),
      );
      return;
    }

    await finalizePublish();
  }

  function setCampaignInputField(key: keyof IEmailCampaign, max: number = 0) {
    return (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setState({
        ...state,
        campaign: {
          ...state.campaign,
          [key]: max > 0 ? e.target?.value.substring(0, max) : e.target?.value,
        },
      });
    };
  }

  function addAudienceSelected(value: number) {
    if (value === -1) {
      history.push('/admin/email-marketing/campaigns/smart-list/new?type=smart&campaign_id=' + state.campaign.id);
    }

    if (selectedAudienceIds.includes(value)) {
      setSelectedAudienceIds(selectedAudienceIds.filter(id => id !== value));
    } else {
      setSelectedAudienceIds(selectedAudienceIds.concat([value]));
    }

  }

  async function loadAudiences() {
    const resp = await GetAudiences(null, false);

    setAudiences(resp.data);
  }

  async function handleDesignEmail() {
    if (state.campaign.id < 1) {
      dispatch(showError(t('Could not find email campaign')));
      return;
    }

    const result = await prepareEmailCampaignBuilderLaunch({
      campaignId: state.campaign.id,
      savePrerequisites: () => updateStep2(false),
      getDesignStatus: campaignId => getEmailCampaignDesignStatus(campaignId, true),
    });

    if (result.ok === false) {
      if (result.message.length > 0) {
        dispatch(showError(result.message));
      }
      return;
    }

    if (result.destination === "select_template") {
      goToStep(Steps.SelectTemplate);
      return;
    }

    history.push(emailCampaignBuilderPath(state.campaign.id), {
      returnTo: emailCampaignBuilderReturnPath(state.campaign.id, "create"),
    });
  }

  async function loadCampaign() {
    if (!isEdit) {
      return;
    }

    const resp = await GetEmailCampaigns({ id: campaignId }, false);
    if (resp.status === StatusCode.OK) {
      if (resp.data.length === 0) {
        dispatch(showError('Could not find email campaign'));
        history.push('/admin/email-marketing/campaigns');
        return;
      }

      const loadedCampaign = resp.data.pop();
      if (loadedCampaign == null) {
        dispatch(showError('Could not find email campaign'));
        history.push('/admin/email-marketing/campaigns');
        return;
      }

      const campaign: IPageState['campaign'] = { ...loadedCampaign };

      setState(prev => ({
        ...prev,
        campaign: {
          ...prev.campaign,
          ...campaign,
        },
      }));
      setSelectedAudienceIds(campaign.audiences?.map(a => a.id) ?? []);

      if (stepNumber === Steps.Success && !isPublishedEmailCampaignStatus(campaign.status)) {
        goToStep(Steps.Schedule);
      }
    }
  }

  async function createNewCampaign() {
    if (creatingCampaign) {
      return;
    }

    setCreatingCampaign(true);
    try {
      const resp = await PostEmailCampaign({}, true);
      if (resp.status !== StatusCode.OK) {
        dispatch(showError(resp.message));
        return;
      }

      history.push(`/admin/email-marketing/campaigns/${resp.data.data.id}/new`);
    } finally {
      setCreatingCampaign(false);
    }
  }

  function viewCampaign() {
    history.push(`/admin/email-marketing/campaigns/${state.campaign.id}`);
  }

  useEffect(() => {
    void loadAudiences();
    void loadCampaign();
  }, [campaignId, location.key]);

  const selectedAudiences = audiences.filter(a => selectedAudienceIds.includes(a.id));
  const campaignAudiences = (state.campaign.audiences?.length ?? 0) > 0
    ? state.campaign.audiences ?? []
    : selectedAudiences;
  const designStepPreview = emailCampaignDesignStepPreviewLabel({
    editable: true,
    design: state.campaign.design,
  });
  const designCtaLabel = emailCampaignDesignCtaLabel(state.campaign.design);
  const designStepBadgeType = emailCampaignDesignStepBadgeType(state.campaign.design);

  function accordionSection(sectionIndex: 0 | 1 | 2) {
    return emailCampaignAccordionSectionLifecycle({
      sectionIndex,
      snapshotsRef: accordionSnapshotsRef,
      getStep2Fields: () => ({
        selectedAudienceIds,
        campaign: state.campaign,
      }),
      setSelectedAudienceIds,
      setCampaignState: setState,
    });
  }

  const accordionItems = [
    {
      badge: t('Step 1'),
      expandable: true,
      success_badge: selectedAudienceIds.length > 0,
      title: t('Choose a smart list'),
      ...accordionSection(0),
      success_preview: <div className="text-sm text-gray-600">
        {selectedAudiences.map(a => a.title).join(', ') ?? t('No customers')}
      </div>,
      onSave: async () => {
        if (selectedAudienceIds.length < 1) {
          dispatch(showError(t('A smart list is required.')));
          return false;
        }

        return await updateStep2();
      },
      content: <FormLayout>
        <FormLayout.Group>
          <div className="grid lg:grid-cols-2">

            <SelectNew
              id="audiences"
              placeholder={t("Select one or more lists")}
              multiValue={selectedAudienceIds}
              label={t("Send to")}
              options={audiences.map(a => ({ label: a.title, id: a.id })).concat([{ id: -1, label: t('+ New Smart List') }])}
              onChange={addAudienceSelected}
            >
            </SelectNew>
          </div>
        </FormLayout.Group>
        {selectedAudienceIds.length > 0 && <FormLayout.Group>
          <div className="text-sm">
            <span className="font-medium">{selectedAudiences.map(a => a.included_customer_count).reduce((sum: number, count) => sum + count, 0)}</span> {t('customers matched in the smart lists')} • <span className="font-medium cursor-pointer" onClick={() => setSegmentModalOpen(true)}>{t('View Customers')}</span>
          </div>
        </FormLayout.Group>}
      </FormLayout>,
    },
    {
      badge: t('Step 2'),
      expandable: true,
      success_badge: (state.campaign.from_address?.length ?? 0) > 0 && (state.campaign.from_name?.length ?? 0) > 0,
      success_preview: <div className="text-sm text-gray-600">
        {state.campaign.from_name}, {state.campaign.from_address}{'@teeon.com'}
      </div>,
      title: t('From'),
      ...accordionSection(1),
      onSave: async () => {
        if (state.campaign?.from_name == null || state.campaign.from_name?.length < 2) {
          dispatch(showError(t('From name is required.')));
          return false;
        }
        return await updateStep2();
      },
      content: <FormLayout>
        <div className="grid grid-cols-2">
          <FormLayout.Group>
            <Input
              label={t("From Name")}
              placeholder={t("Name")}
              value={state.campaign?.from_name ?? ''}
              onChange={setCampaignInputField("from_name")}
            />
          </FormLayout.Group>
          <FormLayout.Group>
            <Input
              className="text-right"
              label={t("Email")}
              placeholder={t("info")}
              value={state.campaign?.from_address ?? ''}
              onChange={setCampaignInputField("from_address")}
              suffix="@teeon.com"
            />
          </FormLayout.Group>
        </div>
      </FormLayout>,
    },
    {
      badge: t('Step 3'),
      expandable: true,
      onSave: async () => {
        if (state.campaign.subject == null || state.campaign.subject.length < 1) {
          dispatch(showError(t('A subject is required.')));
          return false;
        }

        if ((state.campaign.text_preview ?? '').length > TEXT_PREVIEW_MAX_LENGTH) {
          dispatch(showError(t('Text preview must be 255 characters or fewer.')));
          return false;
        }

        return await updateStep2();
      },
      success_badge: (state.campaign.subject?.length ?? 0) > 0,
      success_preview: <div className="text-sm text-gray-600">
        <span className="font-medium">{t('Subject line')}:</span> {state.campaign.subject} • <span className="font-medium">{t('Preview text')}:</span> {state.campaign.text_preview}
      </div>,
      title: t('Subject and preview text'),
      ...accordionSection(2),
      content: <FormLayout>
        <FormLayout.Group>
          <Input
            label={t("Subject")}
            placeholder={t("Subject")}
            value={state.campaign?.subject ?? ''}
            onChange={setCampaignInputField("subject")}
          />
        </FormLayout.Group>
        <FormLayout.Group>
          <Input
            label={t("Text Preview")}
            placeholder={t("Text Preview")}
            value={state.campaign?.text_preview ?? ''}
            onChange={setCampaignInputField("text_preview")}
            maxLength={TEXT_PREVIEW_MAX_LENGTH}
            showCharacterCount
            error={(state.campaign?.text_preview ?? '').length > TEXT_PREVIEW_MAX_LENGTH}
          />
        </FormLayout.Group>
      </FormLayout>,
    },
    {
      badge: t('Step 4'),
      expandable: false,
      success_badge: designStepBadgeType === "success",
      badge_type: designStepBadgeType,
      success_preview: (
        <div className="text-sm text-gray-600">
          {t(designStepPreview)}
        </div>
      ),
      title: t('Email content'),
      action: (
        <ButtonNew
          type="primary"
          size="small"
          onClick={() => {
            void handleDesignEmail();
          }}
        >
          {t(designCtaLabel)}
        </ButtonNew>
      ),
    },
  ];

  async function nextStep() {
    if (!(await update())) {
      return;
    }

    if (stepNumber === Steps.Details && resolveEmailCampaignContinueDestination({design: state.campaign.design})) {
      goToStep(stepNumber + 2);
      return;
    }

    goToStep(stepNumber + 1);
  }

  async function saveAndExit() {
    if (await update()) {
      history.push("/admin/email-marketing");
    }
  }

  const step = stepInfo[stepNumber - 1];
  const scheduleActionsDisabled = scheduleSaving || schedulePublishing;
  const isSuccessStep = stepNumber === Steps.Success;

  return <Page
    title={step.title}
    subtitle={step.subtitle}
    primaryAction={isSuccessStep ? undefined : step.primaryAction}
    secondaryActions={isSuccessStep
      ? undefined
      : stepNumber === Steps.Schedule
        ? [{
          content: scheduleSaving ? t("Saving…") : t("Save & exit"),
          action: saveAndExit,
          disabled: scheduleActionsDisabled,
        }]
        : [{
          content: t('Save & Exit'),
          action: saveAndExit,
        }]
    }
    breadcrumbs={[
      {
        label: t('Email Marketing'),
        url: '/admin/email-marketing',
      },
      ...(isSuccessStep
        ? step.breadcrumbs
        : [
          ...step.breadcrumbs,
        ]),
    ]}
  >
    {stepNumber === Steps.Overview &&
      <Card>
        <Card.Section>
          <div className="text-lg font-semibold mb-2">
            {step.form_title}
          </div>

          <div className="pt-2">
            <FormLayout>
              <div className="grid grid-cols-2 items-start">
                <FormLayout.Group>
                  <div className="mt-2">
                    <Input
                      label={t("Campaign Name")}
                      placeholder={t("i.e. Men's League 2025")}
                      value={state.campaign?.name}
                      onChange={setCampaignInputField("name", 100)}
                    />
                    <div className="text-sm text-gray-600 mt-1.5">{state.campaign.name.length}/100 {t("characters")}</div>
                  </div>
                </FormLayout.Group>
              </div>
              <div className="col-span-2">
                <FormLayout.Group>
                  <div>
                    <div className="text-sm font-medium text-gray-600 mb-1.5">{t('Description')}</div>
                    <textarea className="outline-1 outline-gray-300 text-sm rounded-lg! w-full p-2 focus:outline-[#6888C5] focus:shadow-[0px_0px_0px_4px_rgba(213,227,239,1)]" rows={5} placeholder={t("Add a short description for this campaign")} onChange={setCampaignInputField('description', 300)} value={state.campaign.description} />
                    <div className="text-sm text-gray-600">{state.campaign.description.length}/300 {t("characters")}</div>
                  </div>

                </FormLayout.Group>
              </div>
            </FormLayout>
          </div>
        </Card.Section>
      </Card>
    }

    {stepNumber === Steps.Details && <Accordion items={accordionItems} />}

    {stepNumber === Steps.SelectTemplate &&
      emailBuilderCampaignNeedsTemplateSelection(state.campaign.design) &&
      <EmailCampaignTemplatePicker
        campaignId={state.campaign.id}
        returnTo={createCampaignTemplatePickerReturnTo(state.campaign.id, location.state)}
      />
    }

    {stepNumber === Steps.Schedule && (
      <EmailCampaignSchedulePanel
        value={{
          scheduled_type: state.campaign.scheduled_type,
          scheduled_at: state.campaign.scheduled_at,
          scheduled_recurring_type: state.campaign.scheduled_recurring_type,
        }}
        onChange={updateCampaignSchedule}
      />
    )}

    {stepNumber === Steps.Success && (
      <EmailCampaignPublishSuccess
        campaign={state.campaign}
        audiences={campaignAudiences}
        creatingCampaign={creatingCampaign}
        onCreateNewCampaign={() => {
          void createNewCampaign();
        }}
        onViewCampaign={viewCampaign}
      />
    )}

    {selectedAudiences.length > 0 &&
      <Sheet
        size="medium"
        title={t('Customers')}
        supportText={t("Customize your smart list with filters for your targeted list.")}
        onOk={() => setSegmentModalOpen(false)}
        onCancel={() => setSegmentModalOpen(false)}
        okText={t('Close')}
        open={segmentModalOpen}
      >
        <div className="space-y-2">
          {selectedAudiences.map(a =>
            <AudienceCustomersTable
              key={a.id}
              audience={a}
              showAudienceTitle={true}
            />,
          )}
        </div>
      </Sheet>
    }
  </Page>;
}
